import React, { useEffect, useMemo, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { SurveyModel, TSurveyModel } from "@performyard/models";
import { ButtonStandard } from "@performyard/py-components";

import OptionsDrawer from "../../../../../components/OptionsDrawer";
import Pagination from "../../../../../components/Pagination";
import Spacer from "../../../../../components/Spacer";
import ScrollableTable from "../../../../../components/TableLite/TableLiteScrollable";
import { SurveyFilterPanel, SurveyFilters } from "./SurveyFilterPanel";
import SurveysModals from "./SurveysModals";

import { SURVEY_MODALS } from "../store/model";
import { useSetters } from "../store/store";

import { useInternalApiSearchService } from "../../../../../api/hooks/defs/search-service";

import { getFiltersForSearchService } from "../utils";

import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { SURVEYS_TABLE_LIMIT, surveysConfig } from "../config";

type Props = {
	navLinks: { key: string; label: string; href: string }[];
	activeNavigationLinkKey: string;
	showCreateButton?: boolean;
	showDeleteOption?: boolean;
	showFilter: boolean;
	extraFilters?: Record<string, unknown>;
	disabled?: boolean;
	refetchOnMount?: boolean;
};

const SurveysTabLayout = ({
	navLinks,
	activeNavigationLinkKey,
	showCreateButton = false,
	showDeleteOption = false,
	showFilter,
	extraFilters,
	disabled,
	refetchOnMount = false,
}: Props) => {
	const setters = useSetters();

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		searchValue,
		setDebouncedSearch,
		debouncedSearchValue,
		filters,
		setFilters,
	} = useInfoPageTemplateQueryState<SurveyFilters>({
		defaultSortName: "start_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
	});

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const searchFilters = useMemo(
		() => ({
			...getFiltersForSearchService(filters, debouncedSearchValue),
			...extraFilters,
		}),
		[filters, debouncedSearchValue, extraFilters]
	);

	const {
		data: surveys,
		isLoading,
		refetch,
	} = useInternalApiSearchService({
		service: "survey",
		filters: searchFilters,
		skip: (pageNumber - 1) * SURVEYS_TABLE_LIMIT,
		limit: SURVEYS_TABLE_LIMIT,
		sort: { name: sort.name, direction: sort.direction },
		disabled,
	});

	const parsedSurveysResults = useMemo(() => {
		return (surveys?.results || []).map((survey) => SurveyModel.parse(survey));
	}, [surveys]);

	useEffect(() => {
		if (refetchOnMount && !isLoading) {
			refetch();
		}
	}, []);

	const handleOptionSelection = (option: { id: string; label: string }, survey: TSurveyModel) => {
		setters.setSurveyId(survey._id.toString());
		setters.setSurveyLink(survey.surveyLink ? `${window.location.origin}${survey.surveyLink}` : "");
		setters.setSurveyName(survey.name);
		if (option.id === "copy-link") {
			setters.setModal(SURVEY_MODALS.SURVEY_LINK);
		} else if (option.id === "copy") {
			setters.setModal(SURVEY_MODALS.COPY_SURVEY);
		} else if (option.id === "close") {
			setters.setModal(SURVEY_MODALS.CLOSE_SURVEY);
		} else if (option.id === "delete") {
			setters.setModal(SURVEY_MODALS.DELETE_SURVEY);
		}
	};

	const columns = [
		...surveysConfig.allSurveysStaticTableCols,
		{
			id: "options",
			label: "",
			width: 40,
			isSortable: false,
			getter: (survey: TSurveyModel) => {
				const { status } = survey;
				const options = [
					...(["active", "scheduled"].includes(status)
						? [{ id: "copy-link", label: "Copy Survey Link" }]
						: []),
					{ id: "copy", label: "Save as Copy" },
					...(["active", "scheduled"].includes(status) ? [{ id: "close", label: "Close Survey" }] : []),
					...(showDeleteOption ? [{ id: "delete", label: "Delete Survey" }] : []),
				];
				return (
					<div className="flex justify-end">
						<OptionsDrawer
							horizontal
							optionWidth={110}
							options={options}
							openOn="bottom-end"
							onSelect={(option) => handleOptionSelection(option, survey)}
						/>
					</div>
				);
			},
		},
	];

	return (
		<>
			<InfoPageTemplate
				navigationLinks={navLinks}
				actionButtons={
					showCreateButton
						? [
								<ButtonStandard
									key="create-survey"
									color="green"
									iconLeft={faPlus}
									onClick={() => setters.setModal("create-survey")}
								>
									Create Survey
								</ButtonStandard>,
							]
						: []
				}
				toolbarActionRow={
					<StandardToolbarActionRow
						searchInput={searchValue}
						onSearchInputChange={setDebouncedSearch}
						searchPlaceholder="Search surveys..."
						onFilterButtonPress={showFilter ? () => setIsFiltersPanelOpen(true) : undefined}
						activeFiltersCount={showFilter ? Object.keys(filters).length : undefined}
					/>
				}
				activeNavigationLinkKey={activeNavigationLinkKey}
				mainContent={
					<div data-testid="list-surveys-table">
						<ScrollableTable
							columns={columns}
							rows={parsedSurveysResults}
							isLoading={isLoading}
							activeSort={sort.name}
							activeSortDirection={sort.direction}
							onColumnClick={(column: string) =>
								setSort({ name: column, direction: sort.direction === "asc" ? "desc" : "asc" })
							}
							emptyChildren={<EmptySearchTableMessage mainText="No Surveys Found" />}
						/>
						<Spacer height={3} />
						<Pagination
							pageCount={Math.ceil((surveys?.total || SURVEYS_TABLE_LIMIT) / SURVEYS_TABLE_LIMIT)}
							currentPage={pageNumber}
							onClick={(page: number) => setPageNumber(page)}
						/>
						<Spacer height={2} />
					</div>
				}
				pageTitle="Surveys"
				drawer={
					showFilter ? (
						<SurveyFilterPanel
							isOpen={isFiltersPanelOpen}
							onClose={() => setIsFiltersPanelOpen(false)}
							filters={filters}
							onApplyFilters={setFilters}
						/>
					) : undefined
				}
			/>
			<SurveysModals refetch={refetch} />
		</>
	);
};

export default SurveysTabLayout;
